import {
  VersionedDialog,
  VersionedDialogSection,
} from '@/components/admin/versioned/versioned-dialog';
import { replaysWord } from '@/lib/npc-launch';

/**
 * « Arrêter la série ? » (npc.md §9, §7.2, H13 ; maquette `Serie`, état a) : the box of the one
 * gesture of the page that cuts what is running, and it SHOWS THE EFFECT before the click - the
 * replays that run are interrupted, the ones that wait are closed - as every box of this application
 * does.
 *
 * The stop may take up to thirty seconds: the runner lets each replay finish the turn it has in
 * flight before it interrupts it. While it does, the box stays open, « Annuler » is inert (the
 * request has left, and cancelling could not call it back) and the box says what it waits for, so
 * the operator is told the wait is the gesture and not a freeze.
 */

export interface SeriesStopDialogProps {
  running: number;
  waiting: number;
  /** A series prepared for Claude has no turn to wait for: the slots are only closed. */
  claude: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function SeriesStopDialog({
  running,
  waiting,
  claude,
  busy,
  error,
  onConfirm,
  onCancel,
}: SeriesStopDialogProps) {
  return (
    <VersionedDialog
      data-testid="npc-boite-arret"
      title="Arrêter la série ?"
      description="Ce qui tourne s’interrompt, ce qui attend ne partira pas. Les rejeux déjà joués restent lisibles dans Sessions."
      confirmLabel="Arrêter la série"
      confirmTone="destructive"
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <VersionedDialogSection title="Ce qui change">
        <ul className="list-disc space-y-1 pl-5 text-gray-800 text-small">
          <li data-testid="npc-arret-en-cours">
            {running === 0
              ? 'Aucun rejeu ne tourne en ce moment.'
              : `${replaysWord(running)} ${running > 1 ? 'tournent et sont interrompus' : 'tourne et est interrompu'}, signé${running > 1 ? 's' : ''} de l’opérateur qui arrête.`}
          </li>
          <li data-testid="npc-arret-attente">
            {waiting === 0
              ? 'Aucun rejeu n’attend son tour.'
              : `${replaysWord(waiting)} ${waiting > 1 ? 'attendent et sont fermés' : 'attend et est fermé'} : ${waiting > 1 ? 'ils ne partiront' : 'il ne partira'} pas.`}
          </li>
        </ul>
      </VersionedDialogSection>
      <p
        data-testid="npc-arret-attente-longue"
        aria-live="polite"
        className="text-caption text-gray-600 leading-4"
      >
        {busy
          ? 'Arrêt en cours : chaque rejeu termine le tour qu’il joue avant de s’interrompre, ce qui peut prendre jusqu’à 30 secondes.'
          : claude
            ? 'Les emplacements sont fermés d’un coup.'
            : 'L’arrêt attend la fin du tour que joue chaque rejeu : jusqu’à 30 secondes.'}
      </p>
    </VersionedDialog>
  );
}
